import React, { useCallback, useEffect, useMemo } from 'react';

import { forwardParamKey } from '@aline/common';
import type { ApolloLink } from '@apollo/client';
import { makeVar } from '@apollo/client';
import { useReactiveVar } from '@apollo/client/react';
import { DialogActions, DialogContent, Divider, FormHelperText, Typography } from '@mui/material';
import { capitalCase } from 'change-case';
import { isEmpty } from 'ramda';
import {
  AccountsAutocomplete,
  AlineButton,
  AlineDialog,
  AlineTextarea,
  AlineTooltip,
} from 'src/components';
import { useSelectCurrentUser } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import { updateUrlWithoutRefresh, useDocumentParams } from 'src/helpers';
import {
  useCheckWorkflowAuthorization,
  useGetDocumentForwardingSettings,
  useGetOrgMembers,
  useNotificationDispatch,
} from 'src/hooks';
import { NotificationsActionType } from 'src/notifications';
import type { DeepNonNullable } from 'ts-essentials';
import { makeStyles } from 'tss-react/mui';
import { v4 as uuid4 } from 'uuid';
import { z } from 'zod';

type EmailList = string[];
export const EmailListSchema = z.array(z.string().trim().email());

interface ForwardDocumentDialogState {
  // Array of email addresses.
  autocompleteValue: EmailList;
  message: string;
  open: boolean;
}

export const initialForwardDocumentDialogState: ForwardDocumentDialogState = {
  autocompleteValue: [],
  message: '',
  open: false,
};

export const forwardDocumentDialogStateVar = makeVar<ForwardDocumentDialogState>(
  initialForwardDocumentDialogState
);

export function useForwardDocumentDialogState(): ForwardDocumentDialogState {
  return useReactiveVar(forwardDocumentDialogStateVar);
}

export function setForwardDocumentDialogState(
  stateOrCallback:
    | Partial<ForwardDocumentDialogState>
    | ((state: ForwardDocumentDialogState) => ForwardDocumentDialogState)
): void {
  const existingState = forwardDocumentDialogStateVar();
  const newState =
    typeof stateOrCallback === 'function' ? stateOrCallback(existingState) : stateOrCallback;
  forwardDocumentDialogStateVar({ ...existingState, ...newState });
}

const useStyles = makeStyles()(theme => ({
  container: {
    width: theme.dialogStyles.mediumDialogWidth,
  },
  // Box sizing border-box and a bit of padding is needed to prevent a weird horizontal scroll on the dialog content.
  content: {
    boxSizing: 'border-box',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(1),
    width: '100%',
  },
  forwardButton: {
    marginLeft: theme.spacing(1),
  },
  textArea: {
    flexGrow: 1,
  },
}));

export function ForwardDocumentDialog(): JSX.Element | null {
  const { classes } = useStyles();
  const dispatch = useNotificationDispatch();
  const { orgMembers } = useGetOrgMembers({ variables: { filter: Apollo.OrgMemberFilter.Active } });
  const currentUser = useSelectCurrentUser();
  const { documentId } = useDocumentParams();
  const { autocompleteValue, message, open } = useForwardDocumentDialogState();
  const { document, forwarding, forwardingEntity, handleForwardDocument, loading } =
    useForwardDocument(documentId);
  const filteredUsers = orgMembers.filter(user => {
    const isOwner = user.id === document?.owner.id;
    const isUser = user.id === currentUser?.id;
    return !isUser && !isOwner;
  });
  const autocompleteEmpty = isEmpty(autocompleteValue);
  const parsedEmails = EmailListSchema.safeParse(autocompleteValue);
  const disabledReason = loading
    ? 'Checking authorization'
    : autocompleteEmpty
      ? forwardingEntity
        ? 'Add emails to forward'
        : 'Add recipient emails'
      : !parsedEmails.success
        ? 'Please enter valid emails'
        : '';

  useForwardDocumentLink();

  function handleClose(): void {
    setForwardDocumentDialogState({ open: false });
  }

  async function handleSubmit(): Promise<void> {
    if (!parsedEmails.success) return;
    const { data } = await handleForwardDocument(parsedEmails.data, message);
    if (data) {
      dispatch({
        payload: {
          key: uuid4(),
          message: forwardingEntity
            ? 'Forwarded document successfully.'
            : 'Requested the owner to share this document.',
          variant: 'success',
        },
        type: NotificationsActionType.PUSH_NOTIFICATION,
      });
    }

    handleClose();
  }

  if (!document?.requesterRole) return null;

  const isEditor = document.requesterRole === Apollo.PermissionRole.Editor;
  const msg = isEditor
    ? 'You have Editor access and can share this document directly.'
    : forwardingEntity
      ? `You have ${capitalCase(document.requesterRole)} access. Forwarding is enabled, so you can forward this document. Recipients will receive ${capitalCase(forwardingEntity.forwardingPermissionRole)} access.`
      : `You have ${capitalCase(document.requesterRole)} access and cannot share this document directly. You can request the owner to share it on your behalf.`;

  return (
    <AlineDialog
      TransitionProps={{
        onExit: () => {
          // Allow the dialog to close before resetting state to prevent a UI flicker when the Dialog re-renders before closing down.
          setTimeout(() => {
            setForwardDocumentDialogState(initialForwardDocumentDialogState);
          }, 250);
        },
      }}
      classes={{ paperWidthMd: classes.container }}
      open={open}
      title={
        forwardingEntity
          ? `Forward "${document?.name || 'Untitled document'}"`
          : `Ask owner to share "${document?.name || 'Untitled document'}"`
      }
      onClose={handleClose}
    >
      <DialogContent className={classes.content}>
        <Typography color="textSecondary">{msg}</Typography>
        <AccountsAutocomplete
          accounts={filteredUsers}
          value={autocompleteValue.map(email => ({ __typename: 'NewUser', email }))}
          onChange={(_event, value) => {
            const emailList: string[] = value.flatMap(val => {
              return val.__typename === 'Org' ? [] : [val.email.toLowerCase().trim()];
            });
            setForwardDocumentDialogState({
              autocompleteValue: emailList,
            });
          }}
        />
        {!parsedEmails.success && <FormHelperText error>Please enter valid emails</FormHelperText>}
        <AlineTextarea
          className={classes.textArea}
          maxRows={5}
          minRows={5}
          placeholder={
            forwardingEntity ? 'Add a message for recipients...' : 'Add a message for the owner...'
          }
          value={message}
          onChange={e => setForwardDocumentDialogState({ message: e.target.value })}
        />
        <Divider variant="fullWidth" />
      </DialogContent>
      <DialogActions>
        <AlineButton color="primary" variant="outlined" onClick={handleClose}>
          Cancel
        </AlineButton>
        <AlineTooltip arrow placement="top" title={disabledReason}>
          <div className={classes.forwardButton}>
            <AlineButton
              color="primary"
              disabled={Boolean(disabledReason)}
              loading={forwarding}
              variant="contained"
              onClick={handleSubmit}
            >
              {forwardingEntity ? 'Forward' : 'Request Access'}
            </AlineButton>
          </div>
        </AlineTooltip>
      </DialogActions>
    </AlineDialog>
  );
}

type ForwardFuncReturn = Promise<
  ApolloLink.Result<
    | Apollo.ForwardDocumentMutation
    | Apollo.ForwardWorkflowMutation
    | Apollo.RequestAccessToStorableMutation
  >
>;
interface WorkflowForwardingEntity extends DeepNonNullable<
  Pick<Apollo.Workflow, 'forwardingPermissionRole' | 'id' | '__typename'>
> {
  owner: Pick<Apollo.User, 'email' | 'id' | '__typename'>;
}
type DocumentForwardingEntity = DeepNonNullable<
  Pick<Apollo.Document, 'forwardingPermissionRole' | 'id' | '__typename'>
>;
type ForwardingEntity = DocumentForwardingEntity | WorkflowForwardingEntity;

export function useForwardDocument(documentId: UUID): {
  document: ReturnType<typeof useGetDocumentForwardingSettings>['document'];
  forwarding: boolean;
  forwardingEntity: ForwardingEntity | null;
  handleForwardDocument: (
    recipientEmails: string[],
    message?: string
  ) => Promise<ForwardFuncReturn>;
  loading: boolean;
} {
  const { document, loading: loadingDocument } = useGetDocumentForwardingSettings(documentId, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });
  const { loading: loadingWorkflowAuth, workflowAccessLevel } = useCheckWorkflowAuthorization(
    document?.workflow?.id
  );
  const [requestAccessToStorable, { loading: requestingAccess }] =
    Apollo.useRequestAccessToStorableMutation();
  const [forwardDocument, { loading: forwardingDocument }] = Apollo.useForwardDocumentMutation();
  const [forwardWorkflow, { loading: forwardingWorkflow }] = Apollo.useForwardWorkflowMutation();
  const forwardingEntity = useMemo((): ForwardingEntity | null => {
    if (
      document?.workflow?.variant === Apollo.WorkflowVariant.Contract &&
      document.workflow.forwardingPermissionRole &&
      workflowAccessLevel
    )
      return {
        __typename: 'Workflow',
        forwardingPermissionRole: document.workflow.forwardingPermissionRole,
        id: document.workflow.id,
        owner: document.workflow.owner,
      };

    if (document?.forwardingPermissionRole)
      return {
        __typename: 'Document',
        forwardingPermissionRole: document.forwardingPermissionRole,
        id: document.id,
      };

    return null;
  }, [document, workflowAccessLevel]);
  const handleForwardDocument = useCallback(
    async (recipientEmails: string[], message?: string): Promise<ForwardFuncReturn> => {
      if (!forwardingEntity) {
        // Forwarding not enabled - ask the owner to share with the specified emails
        return requestAccessToStorable({
          variables: {
            input: {
              message,
              requestedForEmails: recipientEmails,
              storableIds: [documentId],
            },
          },
        });
      }

      switch (forwardingEntity.__typename) {
        case 'Workflow': {
          return forwardWorkflow({
            variables: {
              input: { message, recipientEmails, workflowId: forwardingEntity.id },
            },
          });
        }
        case 'Document':
          return forwardDocument({
            variables: { input: { documentId: forwardingEntity.id, message, recipientEmails } },
          });
      }
    },
    [documentId, forwardDocument, forwardWorkflow, forwardingEntity, requestAccessToStorable]
  );

  return {
    document,
    forwarding: forwardingDocument || forwardingWorkflow || requestingAccess,
    forwardingEntity,
    handleForwardDocument,
    loading: loadingDocument || loadingWorkflowAuth,
  };
}

/**
 * Checks for `forwardDocument=true` in URL and opens dialog, if present.
 */
export function useForwardDocumentLink() {
  const searchParams = new URLSearchParams(window.location.search);
  const urlForward = searchParams.get(forwardParamKey);

  useEffect(() => {
    if (!urlForward) return;

    setForwardDocumentDialogState({ open: true });

    // Need to clean up the URL otherwise the effect runs again undesirably...
    const url = new URL(window.location.href);
    url.searchParams.delete(forwardParamKey);
    updateUrlWithoutRefresh(url.toString());
  }, [urlForward]);
}
